import React, { useState, useEffect } from 'react';
import { usersAPI } from '../utils/api';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const emptyUser = { username: '', email: '', password: '', role: 'admin', is_active: true };
const emptyEdit = { email: '', role: 'admin', is_active: true, password: '' };
const emptyPwd = { current_password: '', new_password: '', confirm: '' };

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const { user: currentUser } = useAdminAuth();

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyUser);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);

  const [showPwd, setShowPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState(emptyPwd);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      if (error.response?.status === 403) {
        setLoading(false);
        return;
      }
      notify('Ошибка загрузки пользователей', 'error');
    } finally {
      setLoading(false);
    }
  };

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Change my password (works for any logged-in user, no super_admin required)
  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) {
      notify('Пароли не совпадают', 'error');
      return;
    }
    if (pwdForm.new_password.length < 6) {
      notify('Пароль должен быть не короче 6 символов', 'error');
      return;
    }
    try {
      await usersAPI.changeMyPassword(pwdForm.current_password, pwdForm.new_password);
      notify('Пароль успешно изменён');
      setShowPwd(false);
      setPwdForm(emptyPwd);
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка смены пароля', 'error');
    }
  };

  // Create user (super_admin only)
  const handleCreate = async (e) => {
    e.preventDefault();
    if (createForm.password.length < 6) {
      notify('Пароль должен быть не короче 6 символов', 'error');
      return;
    }
    try {
      await usersAPI.create(createForm);
      notify('Пользователь создан');
      setShowCreate(false);
      setCreateForm(emptyUser);
      loadUsers();
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка создания', 'error');
    }
  };

  // Edit user
  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({ email: user.email, role: user.role, is_active: user.is_active, password: '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const payload = { ...editForm };
    if (!payload.password) delete payload.password;
    try {
      await usersAPI.update(editingUser.id, payload);
      notify('Пользователь обновлён');
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка обновления', 'error');
    }
  };

  const handleDelete = async (user) => {
    if (user.username === currentUser.username) {
      notify('Нельзя удалить собственный аккаунт', 'error');
      return;
    }
    if (!window.confirm(`Удалить пользователя "${user.username}"?`)) return;
    try {
      await usersAPI.delete(user.id);
      notify('Пользователь удалён');
      loadUsers();
    } catch (error) {
      notify(error.response?.data?.detail || 'Ошибка удаления', 'error');
    }
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="users-loading">Загрузка...</div>;
  }

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="users-management">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление пользователями</h1>
          <p className="text-gray-600 mt-2">
            Текущий пользователь: <strong>{currentUser?.username}</strong> ({currentUser?.role === 'super_admin' ? 'Супер-админ' : 'Админ'})
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowPwd(true)}
            className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium"
            data-testid="open-change-password-btn"
          >
            🔑 Сменить мой пароль
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setShowCreate(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium"
              data-testid="create-user-btn"
            >
              + Создать пользователя
            </button>
          )}
        </div>
      </div>

      {notification && (
        <div
          className={`p-4 rounded-lg ${
            notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
          data-testid="users-notification"
        >
          {notification.message}
        </div>
      )}

      {!isSuperAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <strong>Доступ ограничен:</strong> Только супер-администратор может управлять списком пользователей.
            Но вы можете изменить свой собственный пароль кнопкой выше.
          </p>
        </div>
      )}

      {isSuperAdmin && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Имя</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Роль</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Последний вход</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                      user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role === 'super_admin' ? 'Супер-админ' : 'Админ'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString('ru-RU') : 'Никогда'}
                  </td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => openEdit(user)}
                      className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                      data-testid={`edit-user-${user.username}`}
                    >
                      Редактировать
                    </button>
                    {user.username !== currentUser.username && (
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-red-600 hover:text-red-900 font-medium text-sm"
                        data-testid={`delete-user-${user.username}`}
                      >
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Change-my-password modal */}
      {showPwd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Сменить мой пароль</h2>
              <button onClick={() => setShowPwd(false)} className="text-gray-500 text-2xl">×</button>
            </div>
            <form onSubmit={handleChangePwd} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Текущий пароль *</label>
                <input
                  type="password"
                  value={pwdForm.current_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Новый пароль *</label>
                <input
                  type="password"
                  value={pwdForm.new_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">Минимум 6 символов</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Подтвердите новый пароль *</label>
                <input
                  type="password"
                  value={pwdForm.confirm}
                  onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowPwd(false)} className="px-4 py-2 border rounded-lg">
                  Отмена
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg" data-testid="submit-change-password">
                  Сменить пароль
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create user modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Новый пользователь</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-500 text-2xl">×</button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Имя пользователя *</label>
                <input
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="create-username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="create-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Пароль *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  required
                  minLength={6}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="create-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="create-role-select"
                >
                  <option value="admin">Админ</option>
                  <option value="super_admin">Супер-админ</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border rounded-lg">
                  Отмена
                </button>
                <button type="submit" className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg" data-testid="submit-create-user">
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Редактировать «{editingUser.username}»</h2>
              <button onClick={() => setEditingUser(null)} className="text-gray-500 text-2xl">×</button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="edit-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="edit-role-select"
                  disabled={editingUser.username === currentUser.username}
                >
                  <option value="admin">Админ</option>
                  <option value="super_admin">Супер-админ</option>
                </select>
                {editingUser.username === currentUser.username && (
                  <p className="text-xs text-gray-500 mt-1">Нельзя изменить свою роль</p>
                )}
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                  disabled={editingUser.username === currentUser.username}
                  data-testid="edit-active-checkbox"
                />
                <label className="ml-2 text-sm text-gray-700">Активен</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Сбросить пароль <span className="text-gray-400 font-normal">(оставить пустым, чтобы не менять)</span>
                </label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  minLength={editForm.password ? 6 : undefined}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Новый пароль (мин. 6 символов)"
                  data-testid="edit-password-input"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 border rounded-lg">
                  Отмена
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg" data-testid="submit-edit-user">
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
